import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton
} from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import { toast } from "../../services/toast";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()((theme) => ({
  intro: {
    marginBottom: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.5,
  },
  seccion: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  porDefecto: {
    color: theme.palette.text.secondary,
  },

  resultados: {
    marginTop: theme.spacing(2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
  },
  cabeceraResultados: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  numero: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  numeroTexto: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  aviso: {
    marginLeft: "auto",
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  enlace: {
    cursor: "pointer",
    textDecoration: "underline",
  },

  caja: {
    marginTop: 0,
    height: 300,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    ...theme.scrollbarStyles,
  },
  fila: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
  },
  filaNombre: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  filaNumero: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  check: {
    padding: 4,
  },
  centrado: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.disabled,
    fontSize: 13,
    textAlign: "center",
    padding: theme.spacing(2),
  },
  icono: {
    fontSize: 36,
    opacity: 0.5,
  },
}));

const ESTADOS = [
  { valor: "open", nombre: "Atendiendo" },
  { valor: "pending", nombre: "Sin atender" },
  { valor: "closed", nombre: "Cerrado" },
];

const ACTIVIDAD = [
  { valor: 0, nombre: "Cualquier fecha" },
  { valor: 7, nombre: "Últimos 7 días" },
  { valor: 30, nombre: "Últimos 30 días" },
  { valor: 90, nombre: "Últimos 90 días" },
  { valor: 365, nombre: "Último año" },
];

const ORIGEN = [
  { valor: "", nombre: "Cualquiera" },
  { valor: "ads", nombre: "De un anuncio" },
];

const ImportFromTicketsDialog = ({ open, onClose, contactListId, onDone }) => {
  const { classes } = useStyles();

  const [tags, setTags] = useState([]);
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  const [tagIds, setTagIds] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [whatsappIds, setWhatsappIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [days, setDays] = useState(0);
  const [soloAnuncios, setSoloAnuncios] = useState(false);

  const [excluidos, setExcluidos] = useState([]);

  const [contando, setContando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [previo, setPrevio] = useState(null);

  const [buscar, setBuscar] = useState("");

  useEffect(() => {
    if (!open) return;

    api.get("/tags/list").then(({ data }) => setTags(data || [])).catch(() => {});
    api.get("/queue").then(({ data }) => setQueues(data || [])).catch(() => {});
    api
      .get("/whatsapp", { params: { session: 0 } })
      .then(({ data }) => setWhatsapps(data || []))
      .catch(() => {});
  }, [open]);

  const filtros = useMemo(
    () => ({
      tagIds,
      queueIds,
      whatsappIds,
      statuses,
      days: Number(days) || 0,
      soloAnuncios,
      buscar: buscar.trim(),
      excluir: excluidos,
    }),
    [
      tagIds,
      queueIds,
      whatsappIds,
      statuses,
      days,
      soloAnuncios,
      buscar,
      excluidos,
    ]
  );

  const hayFiltro =
    tagIds.length > 0 ||
    queueIds.length > 0 ||
    whatsappIds.length > 0 ||
    statuses.length > 0 ||
    Number(days) > 0 ||
    soloAnuncios ||
    buscar.trim().length > 0;

  const contar = useCallback(async () => {
    if (!open || !contactListId || !hayFiltro) {
      setPrevio(null);
      return;
    }

    setContando(true);
    try {
      const { data } = await api.post(
        `/contact-lists/${contactListId}/from-tickets`,
        { filtros, soloContar: true }
      );
      setPrevio(data);
    } catch (err) {
      toastError(err);
      setPrevio(null);
    } finally {
      setContando(false);
    }
  }, [open, contactListId, filtros, hayFiltro]);

  useEffect(() => {
    const t = setTimeout(contar, 400);
    return () => clearTimeout(t);
  }, [contar]);

  const alternarExcluido = (numero) =>
    setExcluidos((prev) =>
      prev.includes(numero)
        ? prev.filter((n) => n !== numero)
        : [...prev, numero]
    );

  const importar = async () => {
    setImportando(true);
    try {
      const { data } = await api.post(
        `/contact-lists/${contactListId}/from-tickets`,
        { filtros }
      );

      toast.success(
        data.yaEstaban > 0
          ? `${data.agregados} contactos agregados. ${data.yaEstaban} ya estaban en la lista.`
          : `${data.agregados} contactos agregados.`
      );

      if (onDone) onDone(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setImportando(false);
    }
  };

  const multiple = (label, valor, setValor, opciones, claveId, claveNombre) => (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        multiple
        displayEmpty
        notched
        label={label}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        renderValue={(sel) =>
          sel.length === 0 ? (
            <span className={classes.porDefecto}>Todas</span>
          ) : (
            sel
              .map(
                (v) =>
                  opciones.find((o) => String(o[claveId]) === String(v))?.[
                    claveNombre
                  ] || v
              )
              .join(", ")
          )
        }
        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
      >
        {opciones.length === 0 && (
          <MenuItem disabled value="">
            Sin opciones
          </MenuItem>
        )}
        {opciones.map((o) => (
          <MenuItem key={o[claveId]} value={o[claveId]}>
            <Checkbox size="small" checked={valor.indexOf(o[claveId]) > -1} />
            <ListItemText primary={o[claveNombre]} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const simple = (label, valor, setValor, opciones) => (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        displayEmpty
        notched
        label={label}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
      >
        {opciones.map((o) => (
          <MenuItem key={String(o.valor)} value={o.valor}>
            {o.nombre}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const contenido = () => {
    if (!hayFiltro) {
      return (
        <div className={classes.centrado}>
          <PeopleOutlineIcon className={classes.icono} />
          <span>Los contactos aparecerán aquí</span>
        </div>
      );
    }

    if (contando && !previo) return null;

    if (!previo?.muestra?.length) {
      return (
        <div className={classes.centrado}>
          <span>Ningún ticket coincide. Prueba a quitar algún filtro.</span>
        </div>
      );
    }

    return (
      <>
        {previo.muestra.map((m) => (
          <div key={m.number} className={classes.fila}>
            <Checkbox
              size="small"
              className={classes.check}
              checked={!excluidos.includes(m.number)}
              onChange={() => alternarExcluido(m.number)}
            />
            <span className={classes.filaNombre}>{m.name || "Sin nombre"}</span>
            <span className={classes.filaNumero}>{m.number}</span>
          </div>
        ))}

        {previo.encontrados > previo.muestra.length && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", p: 1.5 }}
          >
            y {previo.encontrados - previo.muestra.length} contactos más, que
            también se agregarán.
          </Typography>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Traer contactos desde tus tickets</DialogTitle>

      <DialogContent dividers>
        <div className={classes.intro}>
          Busca entre las conversaciones que ya tuviste y agrega esos contactos a
          la lista. A diferencia de un Excel, aquí <b>ya sabes que el número
          existe</b> y que la persona te escribió alguna vez.
        </div>

        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Buscar un contacto por nombre o número…"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          sx={{ mb: 2.5 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
              endAdornment: buscar ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setBuscar("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <div className={classes.seccion}>O tráelos en bloque</div>

        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {multiple("Etiquetas", tagIds, setTagIds, tags, "id", "name")}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {multiple("Departamentos", queueIds, setQueueIds, queues, "id", "name")}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {multiple(
              "Conexiones",
              whatsappIds,
              setWhatsappIds,
              whatsapps,
              "id",
              "name"
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {multiple(
              "Estado",
              statuses,
              setStatuses,
              ESTADOS,
              "valor",
              "nombre"
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {simple("Actividad", days, setDays, ACTIVIDAD)}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {simple(
              "Origen",
              soloAnuncios ? "ads" : "",
              (v) => setSoloAnuncios(v === "ads"),
              ORIGEN
            )}
          </Grid>
        </Grid>

        <div className={classes.resultados}>
          <div className={classes.cabeceraResultados}>
            <span className={classes.numero}>
              {previo?.encontrados ?? 0}
            </span>
            <span className={classes.numeroTexto}>
              {previo?.encontrados === 1
                ? "contacto se agregará"
                : "contactos se agregarán"}
            </span>

            {contando && <CircularProgress size={16} />}

            {excluidos.length > 0 && (
              <span className={classes.aviso}>
                {excluidos.length} quitado{excluidos.length === 1 ? "" : "s"} ·{" "}
                <span
                  onClick={() => setExcluidos([])}
                  className={classes.enlace}
                >
                  deshacer
                </span>
              </span>
            )}
          </div>

          <div className={classes.caja}>{contenido()}</div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={importando}>
          Cancelar
        </Button>
        <Button
          onClick={importar}
          color="primary"
          variant="contained"
          disableElevation
          disabled={importando || contando || !previo?.encontrados}
        >
          {importando ? "Agregando…" : "Agregar a la lista"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportFromTicketsDialog;
